import { useCallback, useEffect, useState } from "react";
import { errorText } from "../../i18n/serverErrors";
import { shopModeApi } from "../../api/shopMode";
import type { BouquetTemplate } from "../../api/types";
import { ShopFloorBouquetThumb } from "./ShopFloorBouquetThumb";
import { ShopFloorIcon } from "./ShopFloorIcon";
import { useT } from "../../i18n/LanguageProvider";

interface ShopFloorBouquetTemplatePanelProps {
  onBuildNew(): void;
  onSelectTemplate(template: BouquetTemplate): void;
}

export function ShopFloorBouquetTemplatePanel({
  onBuildNew,
  onSelectTemplate,
}: ShopFloorBouquetTemplatePanelProps) {
  const { t } = useT();
  const [templates, setTemplates] = useState<BouquetTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setTemplates(await shopModeApi.floorListBouquetTemplates());
    } catch (err: unknown) {
      setTemplates([]);
      setError(
        errorText(err, "templates.couldNotLoad"),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadTemplates();
  }, [loadTemplates]);

  return (
    <section className="card shop-floor-bouquet-template-panel" aria-busy={loading}>
      <div className="shop-floor-bouquet-template-header">
        <div>
          <h2>{t("hub.buildBouquet")}</h2>
          <p className="muted">{t("templates.chooseOrStart")}</p>
        </div>
        <button type="button" className="button" onClick={onBuildNew}>
          <ShopFloorIcon name="plus" size={20} />
          {t("templates.buildNew")}
        </button>
      </div>

      {error ? (
        <div className="alert error">
          <span>{error}</span>
          <button type="button" className="button plain" onClick={() => void loadTemplates()}>
            {t("templates.retry")}
          </button>
        </div>
      ) : null}

      {loading ? (
        <p className="muted">{t("templates.loading")}</p>
      ) : templates.length === 0 ? (
        <div className="empty-state">{t("templates.none")}</div>
      ) : (
        <div className="sf-bouquet-template-grid">
          {templates.map((template) => (
            <button
              key={template.id}
              type="button"
              className="sf-bouquet-template-card"
              onClick={() => onSelectTemplate(template)}
            >
              <div className="sf-bouquet-template-thumb">
                <ShopFloorBouquetThumb
                  id={template.id}
                  imageKey={template.imageKey}
                  alt={template.name}
                  kind="template"
                />
              </div>
              <span className="sf-bouquet-template-name">{template.name}</span>
              <span className="sf-bouquet-template-meta muted">
                {t("templates.itemCount", { count: template.lines?.length ?? 0 })}
              </span>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
